import * as Sentry from "@sentry/react";
import { AxiosError } from "axios";
import type { ErrorEvent, EventHint } from "@sentry/browser";

const axiosErrorsToIgnore: string[] = [
    AxiosError.ERR_CANCELED,
    AxiosError.ERR_NETWORK,
    AxiosError.ERR_BAD_REQUEST,
] as const;

/**
 * Filters Sentry error events, specifically handling Axios errors.
 *
 * This function is used as a `beforeSend` hook in Sentry initialization.
 * It checks if the error is an AxiosError and if its error code is in the
 * list of errors to ignore (defined in `axiosErrorsToIgnore`).
 *
 * @param {ErrorEvent} event - The error event captured by Sentry.
 * @param {EventHint} hint - Additional information about the error event.
 *
 * @returns {ErrorEvent | null} Returns null if the error should be ignored,
 *                               otherwise returns the original error event.
 */
export function filterSentryError(
    event: ErrorEvent,
    hint: EventHint,
): ErrorEvent | PromiseLike<ErrorEvent | null> | null {
    const error = hint?.originalException;

    // Return event if it's not an error
    if (!(error instanceof AxiosError)) return event;

    // Filter axios errors
    const { code } = error;
    if (code && axiosErrorsToIgnore.includes(code)) {
        console.debug("Dropped axios error", code);
        return null;
    }

    return event;
}

/**
 * Initializes Sentry for error logging and monitoring in production environment.
 *
 * The function relies on environment variables:
 * - VITE_SENTRY_ENVIRONMENT: To determine if it's running in production
 * - VITE_VERSION: To set the release version
 *
 * @function
 * @name InitialiseSentry
 * @returns {void}
 */
function InitialiseSentry(environment: string): void {
    // Init sentry error logging if in production
    if (environment === "production") {
        Sentry.init({
            dsn: "https://edf2da98c02047a3884344dea931009e@o1250235.ingest.sentry.io/6414373",
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration(),
            ],
            beforeSend: filterSentryError,
            environment: environment,
            tracesSampleRate: 0.05,
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            release: import.meta.env.VITE_VERSION,
        });
    }
}

export default InitialiseSentry;
